import React, { useId, useMemo, useState } from 'react';
import { visibleAttachments } from './assistantToolResult';

function FileChips({ attachments, onRemove, onDownload }) {
  if (!attachments.length) return null;
  return (
    <ul className="vp-asst-file-chips">
      {attachments.map((file) => (
        <li key={file.id} className={`vp-asst-chip vp-asst-chip-${file.status}`}>
          <span className="vp-asst-chip-name">{file.name}</span>
          {file.status === 'ready' && (file.downloadUrl || file.id) ? (
            <button
              type="button"
              className="vp-asst-download"
              data-testid="assistant-file-download"
              data-href={file.downloadUrl || ''}
              data-name={file.name}
              onClick={() => onDownload && onDownload(file)}
            >
              下載
            </button>
          ) : null}
          {file.status === 'processing' ? <span className="vp-muted">處理中</span> : null}
          {file.status === 'error' ? <span className="vp-asst-source-error">{file.error || '沒有可用來源'}</span> : null}
          {file.sources?.length ? (
            <span className="vp-muted">{file.sources.filter((s) => !s.error).map((s) => s.label).join('；')}</span>
          ) : null}
          {onRemove ? (
            <button type="button" className="vp-asst-chip-x" aria-label={`移除 ${file.name}`} onClick={() => onRemove(file.id)}>✕</button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function fileLimitLabel(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  const mib = value / (1024 * 1024);
  return Number.isInteger(mib) ? `${mib} MiB` : `${mib.toFixed(1)} MiB`;
}

export default function AssistantComposerTools({
  features,
  attachments,
  ownedIds,
  researchEnabled,
  onToggleResearch,
  onUpload,
  onRemoveAttachment,
  onDownloadAttachment,
  uploading,
  uploadError,
  onCreateArtifact,
  onRunAnalysis,
  onGenerateImage,
  busy,
  lastBotText,
  disabled,
}) {
  const [artifactFormat, setArtifactFormat] = useState(features.artifacts.formats[0]?.id || 'pdf');
  const [artifactTitle, setArtifactTitle] = useState('');
  const [artifactContent, setArtifactContent] = useState('');
  const [analysisFileId, setAnalysisFileId] = useState('');
  const [analysisOp, setAnalysisOp] = useState(features.analysis.operations[0]?.id || '');
  const [analysisColumns, setAnalysisColumns] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageConfirmed, setImageConfirmed] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolPanelId = useId();

  const files = useMemo(() => visibleAttachments(attachments, ownedIds), [attachments, ownedIds]);
  const readyFiles = files.filter((file) => file.status === 'ready');
  const uploadLimit = fileLimitLabel(features.files.maxUploadBytes);
  const driveLinks = features.files.googleDrivePublicLinks;

  if (!features?.hasAny) return null;

  const artifactContentValue = artifactContent || lastBotText || '';

  return (
    <div className={`vp-asst-tools${toolsOpen ? ' open' : ' collapsed'}`}>
      <div className="vp-asst-tools-bar">
        <button
          type="button"
          className="vp-asst-tools-disclosure"
          data-testid="assistant-tools-toggle"
          aria-expanded={toolsOpen}
          aria-controls={toolPanelId}
          onClick={() => setToolsOpen((open) => !open)}
        >
          {toolsOpen ? '收合工具' : '工具'}{readyFiles.length ? `（${readyFiles.length} 個檔案）` : ''}
        </button>
        {!toolsOpen && uploadError ? <span className="vp-asst-source-error" role="alert">{uploadError}</span> : null}
      </div>
      {toolsOpen ? <div className="vp-asst-tools-body" id={toolPanelId}>
      {features.research.declared ? (
        <button
          type="button"
          className={`vp-asst-toggle-btn${researchEnabled ? ' on' : ''}`}
          data-testid="assistant-research-toggle"
          aria-pressed={!!researchEnabled}
          disabled={disabled || !features.research.available}
          onClick={() => onToggleResearch(!researchEnabled)}
        >
          {researchEnabled ? '深入查核：開' : '深入查核：關'}（同一 conversation 送出；僅在伺服器回檢查點／可續跑時才接續）
        </button>
      ) : null}

      {features.files.declared ? (
        <div className="vp-asst-tool-block">
          <div className="vp-asst-tool-row">
            <label className="vp-btn">
              上傳檔案
              <input
                type="file"
                multiple
                hidden
                disabled={disabled || uploading || !features.files.available}
                data-testid="assistant-file-input"
                aria-label="上傳給助理的檔案"
                onChange={(e) => {
                  onUpload(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
            {uploading ? <span className="vp-muted">上傳中…</span> : (
              <span className="vp-muted">
                可多選；每個檔案單獨上傳{uploadLimit ? `，每檔最大 ${uploadLimit}` : ''}；只會把已提取來源的檔案送進問題。
                {driveLinks?.available ? ` 也可在訊息貼上公開 Google Drive 連結${driveLinks.maxLinks ? `（每則最多 ${driveLinks.maxLinks} 個）` : ''}；私人檔案需改為「知道連結的任何人可查看」。` : ''}
              </span>
            )}
          </div>
          {uploadError ? <div className="vp-asst-source-error" role="alert">{uploadError}</div> : null}
          <FileChips attachments={files} onRemove={onRemoveAttachment} onDownload={onDownloadAttachment} />
        </div>
      ) : null}

      {features.analysis.declared ? (
        <div className="vp-asst-tool-block">
          <div className="vp-asst-tool-head">數值分析</div>
          <div className="vp-asst-tool-grid">
            <select
              aria-label="分析檔案"
              data-testid="assistant-analysis-file"
              value={analysisFileId}
              disabled={disabled || !readyFiles.length}
              onChange={(e) => setAnalysisFileId(e.target.value)}
            >
              <option value="">選擇已提取的檔案</option>
              {readyFiles.map((file) => (
                <option key={file.id} value={file.id}>{file.name}</option>
              ))}
            </select>
            <select
              aria-label="分析操作"
              data-testid="assistant-analysis-op"
              value={analysisOp}
              disabled={disabled || !features.analysis.available || !features.analysis.operations.length}
              onChange={(e) => setAnalysisOp(e.target.value)}
            >
              {!features.analysis.operations.length ? <option value="">伺服器未提供分析操作</option> : null}
              {features.analysis.operations.map((op) => (
                <option key={op.id} value={op.id}>{op.label}</option>
              ))}
            </select>
            <input
              aria-label="分析欄位"
              placeholder="欄位（選填，逗號分隔）"
              value={analysisColumns}
              disabled={disabled}
              onChange={(e) => setAnalysisColumns(e.target.value)}
            />
            <button
              type="button"
              className="vp-btn"
              data-testid="assistant-analysis-run"
              disabled={disabled || busy === 'analysis' || !analysisFileId || !analysisOp || !features.analysis.available}
              onClick={() => onRunAnalysis({
                fileId: analysisFileId,
                operation: analysisOp,
                columns: analysisColumns.split(',').map((s) => s.trim()).filter(Boolean),
              })}
            >
              {busy === 'analysis' ? '分析中…' : '執行分析'}
            </button>
          </div>
        </div>
      ) : null}

      {features.artifacts.declared ? (
        <div className="vp-asst-tool-block">
          <div className="vp-asst-tool-head">產出檔案</div>
          <div className="vp-asst-tool-grid">
            <select
              aria-label="產物格式"
              data-testid="assistant-artifact-format"
              value={artifactFormat}
              disabled={disabled || !features.artifacts.available}
              onChange={(e) => setArtifactFormat(e.target.value)}
            >
              {features.artifacts.formats.map((fmt) => (
                <option key={fmt.id} value={fmt.id}>{fmt.label}</option>
              ))}
            </select>
            <input
              aria-label="產物標題"
              placeholder="標題"
              value={artifactTitle}
              disabled={disabled}
              onChange={(e) => setArtifactTitle(e.target.value)}
            />
            <textarea
              aria-label="產物內容"
              placeholder="要寫進檔案的內容（預設可用上一則回覆）"
              rows={2}
              value={artifactContentValue}
              disabled={disabled}
              onChange={(e) => setArtifactContent(e.target.value)}
            />
            <button
              type="button"
              className="vp-btn"
              data-testid="assistant-artifact-create"
              disabled={disabled || busy === 'artifact' || !artifactFormat || !(artifactTitle.trim() && artifactContentValue.trim()) || !features.artifacts.available}
              onClick={() => onCreateArtifact({
                format: artifactFormat,
                title: artifactTitle.trim(),
                content: artifactContentValue.trim(),
                filename: `${artifactTitle.trim()}.${artifactFormat}`,
              })}
            >
              {busy === 'artifact' ? '產生中…' : '產生並下載'}
            </button>
          </div>
          <p className="vp-asst-note">產出標示為你提供的內容，不是研究證據。</p>
        </div>
      ) : null}

      {features.images.declared ? (
        <div className="vp-asst-tool-block">
          <div className="vp-asst-tool-head">圖像產生</div>
          {!features.images.available ? (
            <p className="vp-asst-image-reason" data-testid="assistant-image-reason">{features.images.reason}</p>
          ) : null}
          <textarea
            aria-label="圖像提示"
            data-testid="assistant-image-prompt"
            placeholder="描述要產生的圖像"
            rows={2}
            value={imagePrompt}
            disabled={disabled || !features.images.available}
            onChange={(e) => setImagePrompt(e.target.value)}
          />
          <label className="vp-asst-toggle">
            <input
              type="checkbox"
              data-testid="assistant-image-confirm"
              checked={imageConfirmed}
              disabled={disabled || !features.images.available}
              onChange={(e) => setImageConfirmed(e.target.checked)}
              onClick={(e) => setImageConfirmed(e.currentTarget.checked)}
            />
            我確認要依這段提示產生圖像（不會當成已查證事實）
          </label>
          <button
            type="button"
            className="vp-btn"
            data-testid="assistant-image-generate"
            disabled={disabled || busy === 'image' || !features.images.available || !imageConfirmed || !imagePrompt.trim()}
            onClick={() => onGenerateImage({ prompt: imagePrompt.trim(), confirmed: true })}
          >
            {busy === 'image' ? '產生中…' : '產生圖像'}
          </button>
        </div>
      ) : null}
      </div> : null}
    </div>
  );
}
